/**
 * Approval Chain Configurator (M21.13, criterion 20).
 *
 *   /policies/approval-chains                 → workflow-type picker
 *   /policies/approval-chains/:workflowType   → visual flow builder
 *
 * The builder is a vertical list of step cards (kind + selector +
 * guard + SLA), with add / reorder / delete, a live
 * <ApprovalFlowDiagram /> preview, and a Save that PUTs the chain.
 * Save validation (≥1 step, contiguous numbering, selectors carry
 * their id) is enforced server-side and surfaced inline.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { ApprovalFlowDiagram, PageHeader } from "../components/ui";
import { EmployeePicker } from "../components/ui/EmployeePicker";
import { PositionPicker } from "../components/ui/PositionPicker";
import { api, ApiError } from "../lib/api";
import type { ApprovalChainStep } from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";

const _WORKFLOW_TYPES: { value: string; labelKey: string }[] = [
  { value: "leave_request", labelKey: "policies.approval_chains_wf_leave" },
  { value: "loan_application", labelKey: "policies.approval_chains_wf_loan" },
  { value: "excuse_request", labelKey: "policies.approval_chains_wf_excuse" },
  { value: "termination", labelKey: "policies.approval_chains_wf_termination" },
];

const _KINDS: ApprovalChainStep["kind"][] = [
  "immediate_manager",
  "position",
  "employee",
];

function emptyStep(stepNumber: number): ApprovalChainStep {
  return {
    step_number: stepNumber,
    kind: "immediate_manager",
    parallel: false,
  };
}

function WorkflowTypePicker() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("policies.approval_chains_title")}
        subtitle={t("policies.approval_chains_subtitle")}
      />
      <div className="grid gap-3 sm:grid-cols-2">
        {_WORKFLOW_TYPES.map((w) => (
          <Link
            key={w.value}
            to={`/policies/approval-chains/${w.value}`}
            className="rounded-xl border border-border-subtle bg-surface-default p-5 hover:border-brand/40 hover:bg-brand/5"
          >
            <span className="text-base font-semibold text-text-primary">
              {t(w.labelKey)}
            </span>
            <p className="mt-1 text-sm text-text-secondary">
              {t("policies.approval_chains_edit_flow")}
            </p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function ApprovalChainsPage() {
  const { t } = useTranslation();
  const { workflowType } = useParams<{ workflowType: string }>();
  const { legalEntityId: le, gate } = useLegalEntityGate();
  const qc = useQueryClient();
  const [steps, setSteps] = useState<ApprovalChainStep[]>([]);
  const [notes, setNotes] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);

  const chainsQ = useQuery({
    queryKey: ["approval-chains", le, workflowType],
    queryFn: () => api.listApprovalChains(le, workflowType),
    enabled: !!le && !!workflowType,
  });

  // Seed local edit state from the loaded chain (once per load).
  useEffect(() => {
    if (!chainsQ.data) return;
    const chain = chainsQ.data[0];
    if (chain) {
      setSteps(chain.steps);
      setNotes(chain.notes ?? "");
    } else {
      setSteps([emptyStep(1)]);
      setNotes("");
    }
  }, [chainsQ.data]);

  const saveM = useMutation({
    mutationFn: () => {
      const renumbered = steps.map((s, i) => ({
        ...s,
        step_number: i + 1,
      }));
      return api.upsertApprovalChain({
        legal_entity_id: le!,
        workflow_type: workflowType!,
        steps: renumbered,
        notes: notes || null,
      });
    },
    onSuccess: () => {
      setSaveError(null);
      qc.invalidateQueries({
        queryKey: ["approval-chains", le, workflowType],
      });
    },
    onError: (e: unknown) =>
      setSaveError(e instanceof ApiError ? e.detail : String(e)),
  });

  if (!workflowType) return <WorkflowTypePicker />;

  if (gate) return gate;
  if (chainsQ.isLoading) return <Loading />;
  if (chainsQ.error)
    return <ErrorPanel message={(chainsQ.error as Error).message} />;

  const wfMeta = _WORKFLOW_TYPES.find((w) => w.value === workflowType);
  const wfLabel = wfMeta ? t(wfMeta.labelKey) : workflowType;

  function patchStep(idx: number, patch: Partial<ApprovalChainStep>) {
    setSteps((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    );
  }
  function move(idx: number, dir: -1 | 1) {
    setSteps((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("policies.approval_chains_title_for", { wf: wfLabel })}
        subtitle={t("policies.approval_chains_subtitle_tb")}
        breadcrumbs={[
          { label: t("policies.approval_chains_title"), href: "/policies/approval-chains" },
        ]}
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        {/* Editor */}
        <div className="flex flex-col gap-3">
          {steps.map((s, idx) => (
            <div
              key={idx}
              className="rounded-xl border border-border-subtle bg-surface-default p-4"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-text-primary">
                  {t("policies.approval_chains_step_n", { n: idx + 1 })}
                </span>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    aria-label={t("policies.approval_chains_move_up")}
                    className="rounded p-1 text-text-tertiary hover:bg-surface-muted disabled:opacity-30"
                  >
                    <ArrowUp size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(idx, 1)}
                    disabled={idx === steps.length - 1}
                    aria-label={t("policies.approval_chains_move_down")}
                    className="rounded p-1 text-text-tertiary hover:bg-surface-muted disabled:opacity-30"
                  >
                    <ArrowDown size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setSteps((p) => p.filter((_, i) => i !== idx))
                    }
                    aria-label={t("policies.approval_chains_delete_step")}
                    className="rounded p-1 text-rose-500 hover:bg-rose-50"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="block text-sm">
                  <span className="font-medium text-text-secondary">
                    {t("policies.approval_chains_approver_kind")}
                  </span>
                  <select
                    value={s.kind}
                    onChange={(e) =>
                      patchStep(idx, {
                        kind: e.target
                          .value as ApprovalChainStep["kind"],
                      })
                    }
                    className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 text-sm"
                  >
                    {_KINDS.map((k) => (
                      <option key={k} value={k}>
                        {k.replace("_", " ")}
                      </option>
                    ))}
                  </select>
                </label>

                {s.kind === "position" && (
                  <label className="block text-sm">
                    <span className="font-medium text-text-secondary">
                      {t("policies.approval_chains_position")}
                    </span>
                    <div className="mt-1">
                      <PositionPicker
                        legalEntityId={le!}
                        value={s.position_id ?? null}
                        onChange={(p) =>
                          patchStep(idx, {
                            position_id: p?.id ?? null,
                          })
                        }
                      />
                    </div>
                  </label>
                )}
                {s.kind === "employee" && (
                  <label className="block text-sm">
                    <span className="font-medium text-text-secondary">
                      {t("policies.approval_chains_employee")}
                    </span>
                    <div className="mt-1">
                      <EmployeePicker
                        legalEntityId={le!}
                        value={s.employee_id ?? null}
                        onChange={(emp) =>
                          patchStep(idx, {
                            employee_id: emp?.id ?? null,
                          })
                        }
                      />
                    </div>
                  </label>
                )}

                <label className="block text-sm">
                  <span className="font-medium text-text-secondary">
                    {t("policies.approval_chains_guard_expr")}
                  </span>
                  <input
                    type="text"
                    value={s.guard_expression ?? ""}
                    onChange={(e) =>
                      patchStep(idx, {
                        guard_expression: e.target.value || null,
                      })
                    }
                    placeholder="amount > 50000"
                    className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 text-sm"
                  />
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-text-secondary">
                    {t("policies.approval_chains_sla_hours")}
                  </span>
                  <input
                    type="number"
                    min={1}
                    value={s.sla_hours ?? ""}
                    onChange={(e) =>
                      patchStep(idx, {
                        sla_hours: e.target.value
                          ? Number(e.target.value)
                          : null,
                      })
                    }
                    className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 text-sm"
                  />
                </label>
              </div>
              <label className="mt-3 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={s.parallel ?? false}
                  onChange={(e) =>
                    patchStep(idx, { parallel: e.target.checked })
                  }
                  className="h-4 w-4 rounded border-border-subtle"
                />
                <span className="text-text-secondary">
                  {t("policies.approval_chains_parallel")}
                </span>
              </label>
            </div>
          ))}

          <button
            type="button"
            onClick={() =>
              setSteps((p) => [...p, emptyStep(p.length + 1)])
            }
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-border-subtle py-2 text-sm font-medium text-text-secondary hover:bg-surface-muted"
          >
            <Plus size={14} /> {t("policies.approval_chains_add_step")}
          </button>

          <label className="block text-sm">
            <span className="font-medium text-text-secondary">{t("policies.approval_chains_notes")}</span>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 text-sm"
            />
          </label>

          {saveError && <ErrorPanel message={saveError} />}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => saveM.mutate()}
              disabled={saveM.isPending || steps.length === 0}
              className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg hover:opacity-90 disabled:opacity-50"
            >
              {saveM.isPending
                ? t("policies.approval_chains_saving")
                : t("policies.approval_chains_save_chain_long")}
            </button>
          </div>
        </div>

        {/* Live preview */}
        <div>
          <h2 className="mb-2 text-sm font-semibold text-text-primary">
            {t("policies.approval_chains_flow_preview")}
          </h2>
          <ApprovalFlowDiagram steps={steps} />
        </div>
      </div>
    </div>
  );
}
